import { useEffect, useRef } from "react";

export type TelegramWebApp = {
  initData: string;
  initDataUnsafe?: { start_param?: string };
  colorScheme: string;
  platform?: string;
  ready: () => void;
  expand: () => void;
  isVersionAtLeast?: (version: string) => boolean;
  isVerticalSwipesEnabled?: boolean;
  disableVerticalSwipes?: () => void;
  enableVerticalSwipes?: () => void;
  openLink: (url: string) => void;
  openTelegramLink?: (url: string) => void;
  onEvent: (event: string, fn: () => void) => void;
  offEvent: (event: string, fn: () => void) => void;
  BackButton?: {
    show: () => void;
    hide: () => void;
    onClick: (fn: () => void) => void;
    offClick: (fn: () => void) => void;
  };
};

export function openTelegramLink(
  tg: Pick<TelegramWebApp, "initData" | "openTelegramLink"> | undefined,
  url: string,
) {
  if (!tg?.initData || !tg.openTelegramLink) return false;
  try {
    tg.openTelegramLink(url);
    return true;
  } catch {
    return false;
  }
}

export function configureTelegramScrolling(
  tg:
    | Pick<
        TelegramWebApp,
        | "initData"
        | "expand"
        | "isVersionAtLeast"
        | "isVerticalSwipesEnabled"
        | "disableVerticalSwipes"
        | "enableVerticalSwipes"
      >
    | undefined,
) {
  if (!tg?.initData) return () => {};
  tg.expand();
  if (!tg.disableVerticalSwipes || tg.isVersionAtLeast?.("7.7") === false)
    return () => {};
  const restore = tg.isVerticalSwipesEnabled !== false;
  // Disable the Telegram container's minimize gesture, while leaving document scrolling intact.
  tg.disableVerticalSwipes();
  return () => {
    if (restore) tg.enableVerticalSwipes?.();
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

export function useTelegramEnvironment() {
  useEffect(() => {
    const root = document.documentElement;
    const tg = window.Telegram?.WebApp;
    const releaseScrolling = configureTelegramScrolling(tg);
    root.dataset.telegram = String(Boolean(tg?.initData));
    const appearance = () => {
      root.dataset.theme = tg?.initData
        ? tg.colorScheme
        : window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light";
    };
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const viewport = window.visualViewport;
    let restingHeight = viewport?.height || window.innerHeight;
    let restingWidth = window.innerWidth;
    const geometry = () => {
      const height = viewport?.height || window.innerHeight;
      const editing = document.activeElement?.matches(
        "input:not([type=checkbox]):not([type=radio]),textarea,select,[contenteditable=true]",
      );
      const wasKeyboard = root.dataset.keyboard === "true";
      if (restingWidth !== window.innerWidth || (!editing && !wasKeyboard)) {
        restingHeight = height;
        restingWidth = window.innerWidth;
      }
      root.style.setProperty("--app-viewport-height", `${height}px`);
      root.dataset.keyboard = String(
        (!!editing || wasKeyboard) && restingHeight - height > 120,
      );
    };
    appearance();
    geometry();
    tg?.onEvent("themeChanged", appearance);
    media.addEventListener("change", appearance);
    viewport?.addEventListener("resize", geometry);
    window.addEventListener("resize", geometry);
    document.addEventListener("focusin", geometry);
    document.addEventListener("focusout", geometry);
    return () => {
      releaseScrolling();
      tg?.offEvent("themeChanged", appearance);
      media.removeEventListener("change", appearance);
      viewport?.removeEventListener("resize", geometry);
      window.removeEventListener("resize", geometry);
      document.removeEventListener("focusin", geometry);
      document.removeEventListener("focusout", geometry);
      root.style.removeProperty("--app-viewport-height");
      delete root.dataset.keyboard;
      delete root.dataset.telegram;
    };
  }, []);
}

type NativeBackButton = NonNullable<TelegramWebApp["BackButton"]>;
type BackAction = { run: () => void; priority: number };
const backBindings = new WeakMap<
  NativeBackButton,
  { actions: BackAction[]; dispatch: () => void }
>();

export function bindTelegramBackButton(
  tg: Pick<TelegramWebApp, "initData" | "BackButton"> | undefined,
  onBack: () => void,
  priority = 0,
) {
  if (!tg?.initData || !tg.BackButton) return () => {};
  const button = tg.BackButton;
  let binding = backBindings.get(button);
  if (!binding) {
    const actions: BackAction[] = [];
    const dispatch = () => {
      const next = actions.reduce<BackAction | undefined>(
        (selected, action) =>
          !selected || action.priority >= selected.priority ? action : selected,
        undefined,
      );
      next?.run();
    };
    binding = { actions, dispatch };
    backBindings.set(button, binding);
    button.onClick(dispatch);
  }
  const action = { run: onBack, priority };
  binding.actions.push(action);
  button.show();
  return () => {
    const index = binding.actions.indexOf(action);
    if (index < 0) return;
    binding.actions.splice(index, 1);
    if (!binding.actions.length) {
      button.offClick(binding.dispatch);
      button.hide();
      backBindings.delete(button);
    }
  };
}

export function useTelegramBackButton(
  active: boolean,
  onBack: () => void,
  priority = 0,
) {
  const action = useRef(onBack);
  action.current = onBack;
  useEffect(() => {
    if (!active) return;
    return bindTelegramBackButton(
      window.Telegram?.WebApp,
      () => action.current(),
      priority,
    );
  }, [active, priority]);
}
